import React, { createContext, useContext, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const docKey = user?.id || user?.docId || 'anon';

  // Initialize subscription status from localStorage on mount
  React.useEffect(() => {
    if (isAuthenticated && user) {
      const cachedStatus = localStorage.getItem(`subscription_${docKey}`);
      if (cachedStatus) {
        try {
          const parsed = JSON.parse(cachedStatus);
          // Check if cache is still valid (not older than 1 hour)
          const cacheTime = parsed.cachedAt || 0;
          const now = Date.now();
          const oneHour = 60 * 60 * 1000;
          
          if (now - cacheTime < oneHour) {
            setSubscriptionStatus(parsed);
            return; // Use cached data, skip API call
          }
        } catch (e) {
          console.warn('Failed to parse cached subscription status:', e);
        }
      }
    }
  }, [isAuthenticated, user, docKey]);

  // Check subscription status from Cloud Functions with much longer cache
  const { isLoading: statusLoading, error: statusError } = useQuery(
    ['u', docKey, 'subscription-status'],
    () => api.get('/subscription/status').then(res => res.data),
    {
      enabled: isAuthenticated && !subscriptionStatus, // Only fetch if not cached
      retry: false,
      staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes (was 2 minutes)
      cacheTime: 60 * 60 * 1000, // Cache for 1 hour (was 5 minutes)
      refetchOnWindowFocus: false, // Don't refetch when user returns to tab
      refetchOnMount: false, // Don't refetch on remount if data is fresh
      onSuccess: (data) => {
        const statusWithCache = {
          ...data,
          cachedAt: Date.now()
        };
        setSubscriptionStatus(statusWithCache);
        // Cache in localStorage
        localStorage.setItem(`subscription_${docKey}`, JSON.stringify(statusWithCache));
      },
      onError: (error) => {
        console.warn('Subscription status check failed, using fallback:', error.message);
        // Provide fallback trial status
        const fallbackStatus = {
          isValid: true,
          isTrial: true,
          daysRemaining: 15,
          daysExpired: 0,
          status: 'trial',
          cachedAt: Date.now()
        };
        setSubscriptionStatus(fallbackStatus);
        localStorage.setItem(`subscription_${docKey}`, JSON.stringify(fallbackStatus));
      }
    }
  );

  // Renew subscription mutation
  const renewSubscription = { mutateAsync: async () => {} };

  // Sync user data mutation - Phase 1 fallback
  const syncUserData = useMutation(
    async () => {
      // For Phase 1, just return success without calling API
      console.log('User data sync skipped in Phase 1');
      return { success: true };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['u', docKey, 'subscription-status']);
      },
      onError: (error) => {
        console.warn('User data sync failed:', error.message);
      }
    }
  );

  // Get subscription history from Cloud Functions
  const { data: historyData } = useQuery(
    ['u', docKey, 'subscription-history'],
    () => api.get('/subscription/history').then(res => res.data),
    {
      enabled: isAuthenticated, // Fetch when authenticated
      retry: false,
      staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes
      cacheTime: 60 * 60 * 1000, // Cache for 1 hour
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.warn('Subscription history check failed, using fallback:', error.message);
      }
    }
  );

  // Manual refresh function for subscription status
  const refreshSubscription = React.useCallback(() => {
    // Clear cache and refetch
    localStorage.removeItem(`subscription_${docKey}`);
    setSubscriptionStatus(null);
    queryClient.invalidateQueries(['u', docKey, 'subscription-status']);
    queryClient.invalidateQueries(['u', docKey, 'subscription-history']); // Also refresh payment history
  }, [docKey, queryClient]);

  // Load subscription history when needed
  const loadSubscriptionHistory = React.useCallback(() => {
    queryClient.fetchQuery(['u', docKey, 'subscription-history']);
  }, [docKey, queryClient]);

  const value = {
    subscriptionStatus,
    isLoading: statusLoading,
    error: statusError,
    renewSubscription,
    syncUserData,
    refreshSubscription,
    loadSubscriptionHistory,
    history: historyData?.history || [],
    isSubscriptionValid: subscriptionStatus?.isValid || false,
    daysRemaining: subscriptionStatus?.daysRemaining || 0,
    daysExpired: subscriptionStatus?.daysExpired || 0,
    isTrial: subscriptionStatus?.isTrial || false
  };


  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
